import { 
    setupFormCommonBehaviour,
} from ".";

import { 
    InputNameConfigMap,
    FileUploadItemError,
    uploadAttachments,
} from "./file-upload";

import { deletePost, DeletePostConfig } from "./delete-post";

declare const jQuery: any;
const $ = jQuery;

const handlerName: string = "supp-acc-attachment"; // PHP FileUpload class NAME const

const ajaxCall: Function = window["iw"].utils.ajaxCall;
const errorModal: Function = window["iw"].modal.errorModal;
const confirmModal: Function = window["iw"].modal.confirmModal;
const successModal: Function = window["iw"].modal.successModal;
const waitModal: Function = window["iw"].modal.waitModal;
const closeModal: Function = window["iw"].modal.closeModal;
const initCollapsibleSections: Function = window["iw"].initCollapsibleSections;
const reinitWysiwygs: Function = window["iw"].reinitWysiwygs;
const scrollToWithHeaderOffset: Function = window["iw"].layout.scrollToWithHeaderOffset;
const initCustomCheckboxes: Function = window["iw"].initCustomCheckboxes;
const initCustomSelects: Function = window["iw"].initCustomSelects;

export function initEditExpert($main): void
{
    const $mainInner = $main.children(`[data-edit-expert]`);
    if ($mainInner.length === 0)
    {
        return;
    }

    const expertId: number = parseInt($mainInner.attr("data-edit-expert"), 10);
    const supplierId: number = parseInt($mainInner.attr("data-supplier"), 10);

    if (isNaN(expertId) || !supplierId)
    {
        throw new Error(`No expertId/supplierId`);
    }

    const previewUrl: string = $mainInner.attr("data-preview-url");
    const approvalRequested: boolean = !!$mainInner.attr("data-approval-requested");
    
    initMainInner($mainInner, expertId, supplierId, previewUrl, approvalRequested);
}

function initMainInner($mainInner, expertId: number, supplierId: number, previewUrl: string, approvalRequested: boolean): void
{
    const $save = $mainInner.find("[data-main-buttons] [data-save]");
    const $preview = $mainInner.find("[data-main-buttons] [data-preview]");
    const $publish = $mainInner.find("[data-main-buttons] [data-publish]");

    let unsavedChanges = false;

    const onFirstChange = () => {
        $save.removeAttr("disabled");
        $publish.removeAttr("disabled");
        $preview.attr("disabled", "disabled");
        unsavedChanges = true;
    };

    const commonCtrls = setupFormCommonBehaviour($mainInner, onFirstChange);

    // 4-stage server request process:
    // (1) Create supplier post (if not yet created) and update non-file-related post data
    // (2) Upload file attachments
    // (3) Update supplier post with secondary (file-related) post data
    // (4) Get updated form HTML and render/initialize
    
    const buildBasicAjaxData = (rawData, requestApproval: boolean) => {
        return {
            request_approval: requestApproval,
            expert_id: expertId,
            supplier_id: supplierId,
            expert_title: rawData.expert_title,
            expert_company_position: rawData.expert_company_position,
            expert_enquiry_email: rawData.expert_enquiry_email,
        };
    };

    const saveBasicAjaxData = async (basicAjaxData): Promise<{ supplierId: number, expertId: number }> => {
        waitModal(`Submitting expert profile, please wait...`);
        const response = await ajaxCall("supp-acc-update-expert", basicAjaxData);
        if (!response?.supplierId || !response?.expertId) {
            console.error(`saveBasicAjaxData - bad response:`, response);
            throw new Error(`No response.supplierId/response.expertId`);
        }
        return response;
    };

    const rawDataContainsFileUpload = (rawData): boolean => {
        return (rawData.expert_featured_image?.new.length || 0) > 0 ||
            (rawData.expert_featured_image?.saved.length || 0) > 0;
    }

    const uploadPostAttachments = async (rawData, newExpertId: number, newSupplierId: number): Promise<void> => {
        const uploadData = { expert_id: newExpertId, supplier_id: newSupplierId };
        const inputNameConfigMap: InputNameConfigMap = {
            expert_featured_image: { inputType: "images", progressMsg: `Uploading expert image...` }
        };
        await uploadAttachments(handlerName, rawData, inputNameConfigMap, uploadData);
    };

    const buildSecondaryAjaxData = (rawData, newExpertId: number, newSupplierId: number) => {
        return {
            expert_id: newExpertId,
            supplier_id: newSupplierId,
            expert_featured_image: rawData.expert_featured_image
        };
    };

    const saveSecondaryAjaxData = async (secondaryAjaxData): Promise<{ expertId: number, supplierId: number }> => {
        waitModal(`Saving image data, please wait...`);
        const response = await ajaxCall("supp-acc-update-expert", secondaryAjaxData);
        if (!response?.supplierId || !response?.expertId) {
            console.error(`saveSecondaryAjaxData - bad response:`, response);
            throw new Error(`No response.supplierId/response.expertId`);
        }
        return response;
    };

    const getUpdatedMainInnerHtml = async (): Promise<{ html: string, previewUrl: string }> => {
        waitModal(`Preparing preview, please wait...`);
        const response = await ajaxCall("supp-acc-get-form", { form: "edit_expert", supplier_id: supplierId });
        if (!response?.html) {
            console.error(`getUpdatedMainInnerHtml - bad response:`, response);
            throw new Error(`No response.html`);
        }
        return response;
    };

    const resetForm = async (newExpertId: number, newSupplierId: number, requestApproval: boolean) => {
        const response = await getUpdatedMainInnerHtml();
        commonCtrls.cancelWarnBeforePageLeave();
        $mainInner.html($(response.html).html());
        reinitWysiwygs($mainInner.find(`[data-wysiwyg]`));
        initCollapsibleSections();
        initCustomCheckboxes();
        initCustomSelects();
        initMainInner($mainInner, newExpertId, newSupplierId, response.previewUrl, requestApproval);
        scrollToWithHeaderOffset($mainInner);
    };

    const save = async (requestApproval: boolean) => {
        const rawData = commonCtrls.gatherData();
        if (!rawData)
        {
            // User data invalid. Error modal already displayed.
            return;
        }
        const basicAjaxData = buildBasicAjaxData(rawData, requestApproval);
        if (!basicAjaxData)
        {
            throw new Error(`No basicAjaxData`);
        }
        $mainInner.addClass("busy");
        try
        {
            const basicAjaxResponse = await saveBasicAjaxData(basicAjaxData);
            if (!basicAjaxResponse?.supplierId || !basicAjaxResponse?.expertId)
            {
                throw new Error(`No basicAjaxResponse.supplierId/expertId`);
            }
            const newExpertId: number = expertId = basicAjaxResponse.expertId; // set expertId so we do post update instead of insert, in the event that there is a future error during this submission process.
            const newSupplierId: number = supplierId = basicAjaxResponse.supplierId; // set supplierId so we do post update instead of insert, in the event that there is a future error during this submission process.
            if (rawDataContainsFileUpload(rawData)) {
                await uploadPostAttachments(rawData, newExpertId, newSupplierId);
                await saveSecondaryAjaxData(buildSecondaryAjaxData(rawData, newExpertId, newSupplierId));
            }
            resetForm(newExpertId, newSupplierId, requestApproval);
        }
        catch (error)
        {
            if (!(error instanceof FileUploadItemError)) // FileUploadItemError already shows modal before throw
            {
                errorModal(`Failed to save. If the problem persists, please get in touch.`);
            }
            console.error("Save failed:", error);
            return;
        }
        finally
        {
            $mainInner.removeClass("busy");
        }
        successModal(
            requestApproval ? `Your submission has been received and is awaiting approval.` : `Your data has been saved.`,
            undefined,
            5000
        );
    };

    $save.on("click", function (event) {
        save(false);
        return false;
    });

    $publish.on("click", function (event) {
        save(true);
        return false;
    });

    if (previewUrl)
    {
        $preview.removeAttr("disabled").on("click", function (event) {
            const checkAndNavigate = async () => {
                if (unsavedChanges &&
                    !await confirmModal("Preview Expert", "You have unsaved changes which will not show up on the preview. View preview anyway?", "yesno"))
                {
                    return;
                }
                window.open(previewUrl);
            };
            checkAndNavigate();
            return false;
        });
    }
    else
    {
        $preview.attr("title", "You must save before you can preview");
    }

    if (!approvalRequested)
    {
        $publish.removeAttr("disabled");
    }

    // Delete Expert
    $(`[data-delete-expert]`).on("click", function (event) {
        const $button = $(this);
        const postTitle: string = $button.attr("data-post-title");
        const config: DeletePostConfig = {
            confirmMsg: `Are you sure you want to delete "${postTitle}"? This cannot be undone!`,
            successMsg: `You have successfully deleted "${postTitle}".`,
            postId: expertId,
            postType: "expert"
        };
        const del = async () => {
            const deleted: boolean = await deletePost(config);
            if (!deleted)
            {
                return;
            }
            try
            {
                await resetForm(0, supplierId, false);
            }
            catch (error)
            {
                console.error(`Error fetching expert form html after delete:`, error);
                errorModal(`There was a problem resetting the form - please try refreshing the page.`);
                return;
            }
            closeModal();
        };
        del();
        return false;
    });
}
